/**
 * Masinile de stari ale comenzii.
 *
 * Trei masini distincte, care nu au voie sa fie confundate:
 *  1. comanda platformei (`orders.status`) — ce vede clientul;
 *  2. legul catre un vendor (`order_legs.status`) — oglinda comenzii de canal din POS;
 *  3. oprirea de cursa (`run_stops.status`) — forward-only, stari terminale imutabile.
 *
 * Piesa critica e ADR-007: lista statusurilor care produc VENIT e explicita si
 * fail-closed. Un status necunoscut nu contribuie niciodata la bani. Fail-open
 * ramane doar pe numaratorile operationale, unde a rata o comanda pe un ecran e
 * o eroare recuperabila, iar a raporta venit inexistent nu e.
 */
// ---------------------------------------------------------------------------
// 1. Comanda platformei
// ---------------------------------------------------------------------------
export const PLATFORM_ORDER_STATUSES = [
    "quoting",
    "committed",
    "awaiting_assignment",
    "assigned",
    "in_kitchen",
    "ready",
    "picked_up",
    "in_transit",
    "delivered",
    "failed",
    "cancelled",
];
export const TERMINAL_PLATFORM_ORDER_STATUSES = [
    "delivered",
    "failed",
    "cancelled",
];
const PLATFORM_ORDER_TRANSITIONS = {
    quoting: ["committed", "cancelled"],
    committed: ["awaiting_assignment", "in_kitchen", "cancelled", "failed"],
    // Intoarcerea in `committed` din starile de dinainte de pickup e redirectionarea
    // manuala catre alt restaurant (`core/src/orders/redirect.ts`): pickup-ul de la
    // primul s-a stins, iar comanda asteapta din nou confirmarea unei bucatarii.
    // Nu e o „retrogradare" arbitrara — e exact starea in care se afla o comanda
    // trimisa si neconfirmata inca.
    awaiting_assignment: ["assigned", "in_kitchen", "committed", "cancelled", "failed"],
    assigned: ["in_kitchen", "ready", "picked_up", "committed", "cancelled", "failed"],
    in_kitchen: ["ready", "assigned", "committed", "cancelled", "failed"],
    // `ready -> assigned` (ADR-031): mancarea e gata si comanda nu are curier —
    // fie restaurantul a terminat inainte de alocare, fie curierul a refuzat sau a
    // lasat oferta sa expire dupa ce bucataria a marcat „gata". Fara tranzitia
    // asta, exact comenzile cele mai urgente ar ramane fara drum inapoi in
    // dispecerat.
    ready: ["assigned", "picked_up", "committed", "cancelled", "failed"],
    picked_up: ["in_transit", "delivered", "failed"],
    in_transit: ["delivered", "failed"],
    delivered: [],
    failed: [],
    cancelled: [],
};
/**
 * ADR-007, aplicat la nivel de platforma: banii exista din momentul in care
 * marfa a plecat cu bonul fiscal, nu mai devreme. Enumerare explicita, nu
 * "tot ce nu e anulat".
 */
export const REVENUE_PLATFORM_ORDER_STATUSES = [
    "picked_up",
    "in_transit",
    "delivered",
];
export function isPlatformOrderStatus(value) {
    return typeof value === "string" && PLATFORM_ORDER_STATUSES.includes(value);
}
export function isTerminalPlatformOrderStatus(status) {
    return isPlatformOrderStatus(status) && TERMINAL_PLATFORM_ORDER_STATUSES.includes(status);
}
/** Fail-closed: un status necunoscut nu produce venit. */
export function isRevenuePlatformOrder(status) {
    return isPlatformOrderStatus(status) && REVENUE_PLATFORM_ORDER_STATUSES.includes(status);
}
/**
 * Tintele declarate pentru un status, ca lista proprie.
 *
 * Exista ca tabelul de tranzitii sa poata fi VERIFICAT din afara, nu doar
 * consultat indirect prin `canTransitionPlatformOrder`. O tinta scrisa gresit
 * ("picked_ up") nu apare in niciun drum legitim, deci nicio proba de tranzitie
 * nu o atinge; efectul ei se vede abia in productie, ca o comanda blocata in
 * `ready`. Tipul de retur e `string[]`, nu `PlatformOrderStatus[]`: exact asta e
 * ce se verifica, iar un tip care presupune raspunsul ar ascunde intrebarea.
 */
export function platformTransitionsFrom(status) {
    return PLATFORM_ORDER_TRANSITIONS[status];
}
export function canTransitionPlatformOrder(from, to) {
    if (!isPlatformOrderStatus(from) || !isPlatformOrderStatus(to))
        return false;
    return PLATFORM_ORDER_TRANSITIONS[from].includes(to);
}
export function assertPlatformOrderTransition(from, to) {
    if (!isPlatformOrderStatus(from)) {
        throw new Error(`Statusul curent al comenzii nu este recunoscut: "${from}".`);
    }
    if (!isPlatformOrderStatus(to)) {
        throw new Error(`Statusul cerut pentru comanda nu este recunoscut: "${to}".`);
    }
    if (!PLATFORM_ORDER_TRANSITIONS[from].includes(to)) {
        throw new Error(`Tranzitie interzisa pentru comanda: din "${from}" in "${to}". Tranzitii permise din "${from}": ` +
            `${PLATFORM_ORDER_TRANSITIONS[from].join(", ") || "niciuna, statusul este final"}.`);
    }
}
// ---------------------------------------------------------------------------
// 2. Legul catre vendor
// ---------------------------------------------------------------------------
export const ORDER_LEG_STATUSES = [
    "pending",
    "committed",
    "accepted",
    "preparing",
    "ready",
    "picked_up",
    "delivered",
    "rejected",
    "cancelled",
    "superseded",
];
/**
 * `superseded` apare acum si dupa acceptare, pana inclusiv `ready`.
 *
 * Motivul e redirectionarea manuala (`core/src/orders/redirect.ts`): operatorul
 * muta o comanda la alt restaurant, iar pickup-ul de la primul se stinge dupa ce
 * el o acceptase — uneori chiar dupa ce o pregatise. Nu e `cancelled`: comanda
 * clientului traieste mai departe, la alta bucatarie. Distinctia nu e cosmetica,
 * `order_legs_fee_ux` se sprijina pe ea ca legul nou sa poata prelua taxa.
 *
 * Se opreste la `ready`: dupa `picked_up` mancarea e la curier, iar ce urmeaza e
 * un retur, cu documentele lui.
 */
const ORDER_LEG_TRANSITIONS = {
    pending: ["committed", "cancelled", "superseded"],
    committed: ["accepted", "rejected", "cancelled", "superseded"],
    accepted: ["preparing", "ready", "cancelled", "superseded"],
    preparing: ["ready", "cancelled", "superseded"],
    ready: ["picked_up", "cancelled", "superseded"],
    picked_up: ["delivered"],
    delivered: [],
    rejected: [],
    cancelled: [],
    superseded: [],
};
export function isOrderLegStatus(value) {
    return typeof value === "string" && ORDER_LEG_STATUSES.includes(value);
}
export function canTransitionOrderLeg(from, to) {
    if (!isOrderLegStatus(from) || !isOrderLegStatus(to))
        return false;
    return ORDER_LEG_TRANSITIONS[from].includes(to);
}
export function assertOrderLegTransition(from, to) {
    if (!isOrderLegStatus(from)) {
        throw new Error(`Statusul curent al legului nu este recunoscut: "${from}".`);
    }
    if (!isOrderLegStatus(to)) {
        throw new Error(`Statusul cerut pentru leg nu este recunoscut: "${to}".`);
    }
    if (!ORDER_LEG_TRANSITIONS[from].includes(to)) {
        throw new Error(`Tranzitie interzisa pentru legul comenzii: din "${from}" in "${to}". Tranzitii permise din "${from}": ` +
            `${ORDER_LEG_TRANSITIONS[from].join(", ") || "niciuna, statusul este final"}.`);
    }
}
// ---------------------------------------------------------------------------
// 3. Comanda de canal, asa cum o vede POS-ul (ADR-007)
// ---------------------------------------------------------------------------
/**
 * Statusurile care CONTRIBUIE la venit. Lista e inchisa si se modifica doar
 * impreuna cu migratia care schimba semantica statusurilor in POS.
 * `hold` si `hold_cancelled` sunt in mod deliberat absente: un hold nu e o
 * vanzare, e o rezervare de capacitate care poate expira.
 */
export const REVENUE_CHANNEL_ORDER_STATUSES = [
    "accepted",
    "preparing",
    "ready_for_pickup",
    "picked_up",
    "delivered",
];
/**
 * Statusurile despre care stim ca NU sunt venit. Nu e complementul listei de
 * mai sus (nu putem enumera tot ce va inventa POS-ul in viitor), ci lista
 * folosita pentru excluderea explicita din liste, KDS si rapoarte.
 */
export const NON_REVENUE_CHANNEL_ORDER_STATUSES = [
    "hold",
    "hold_cancelled",
    "new",
    "rejected",
    "cancelled",
    "failed",
    "expired",
];
/** Statusurile care nu au ce cauta pe ecranele operationale (ADR-007). */
export const HIDDEN_FROM_CHANNEL_LISTS_STATUSES = ["hold", "hold_cancelled"];
/**
 * FAIL-CLOSED. Un status necunoscut inseamna ca acest cod e mai vechi decat
 * POS-ul care l-a emis; in dubiu, banii nu se numara. Inversul (fail-open) a
 * produs deja incidentul de "venit fantoma" din 2026-07-28.
 */
export function isRevenueChannelOrder(status) {
    return (typeof status === "string" &&
        REVENUE_CHANNEL_ORDER_STATUSES.includes(status));
}
/**
 * FAIL-OPEN, si numai aici: numaratorile operationale (cate comenzi sunt in
 * lucru) prefera sa arate o comanda in plus decat sa ascunda una reala.
 * Statusurile explicit non-operationale raman excluse.
 */
export function isOperationalChannelOrder(status) {
    if (typeof status !== "string" || status.length === 0)
        return false;
    if (HIDDEN_FROM_CHANNEL_LISTS_STATUSES.includes(status))
        return false;
    return !["rejected", "cancelled", "failed", "expired"].includes(status);
}
// ---------------------------------------------------------------------------
// 4. Oprirea de cursa
// ---------------------------------------------------------------------------
export const RUN_STOP_STATUSES = [
    "pending",
    "en_route",
    "arrived",
    "completed",
    "failed",
    "refused",
];
export const TERMINAL_RUN_STOP_STATUSES = ["completed", "failed", "refused"];
const RUN_STOP_TRANSITIONS = {
    pending: ["en_route", "failed"],
    en_route: ["arrived", "failed"],
    arrived: ["completed", "failed", "refused"],
    completed: [],
    failed: [],
    refused: [],
};
export function isRunStopStatus(value) {
    return typeof value === "string" && RUN_STOP_STATUSES.includes(value);
}
/**
 * Forward-only, cu retry permis pe aceeasi stare: app-ul de curier retrimite
 * acelasi eveniment dupa o pierdere de semnal, iar al doilea apel trebuie sa
 * fie un no-op, nu o eroare.
 */
export function canTransitionRunStop(from, to) {
    if (!isRunStopStatus(from) || !isRunStopStatus(to))
        return false;
    if (from === to)
        return !TERMINAL_RUN_STOP_STATUSES.includes(from);
    return RUN_STOP_TRANSITIONS[from].includes(to);
}
export function assertRunStopTransition(from, to) {
    if (!isRunStopStatus(from)) {
        throw new Error(`Statusul curent al opririi nu este recunoscut: "${from}".`);
    }
    if (!isRunStopStatus(to)) {
        throw new Error(`Statusul cerut pentru oprire nu este recunoscut: "${to}".`);
    }
    if (!canTransitionRunStop(from, to)) {
        throw new Error(`Tranzitie interzisa pentru oprirea de cursa: din "${from}" in "${to}". ` +
            (TERMINAL_RUN_STOP_STATUSES.includes(from)
                ? `Statusul "${from}" este final.`
                : `Tranzitii permise din "${from}": ${RUN_STOP_TRANSITIONS[from].join(", ")}.`));
    }
}
// ---------------------------------------------------------------------------
// 5. Statusul documentului fiscal
// ---------------------------------------------------------------------------
/**
 * ADR-006: nu exista `skipped`. Daca bonul nu se poate emite, comanda nu
 * pleaca; nu exista stare in care marfa a plecat fara document fiscal.
 */
export const FISCAL_RECEIPT_STATUSES = ["pending", "issued", "failed", "cancelled"];
export function isFiscalReceiptStatus(value) {
    return typeof value === "string" && FISCAL_RECEIPT_STATUSES.includes(value);
}
/** Predarea catre curier e permisa doar cu bon emis (ADR-006). */
export function canHandoverWithFiscalStatus(status) {
    return status === "issued";
}
//# sourceMappingURL=order-status.js.map